import {
  faFolder,
  faGear,
  faUser,
} from "@fortawesome/pro-solid-svg-icons";
import { useState } from "react";

import {
  DSButton,
  DSCardWrapper,
  DSCheckbox,
  DSDropdown,
} from "../molecules";
import {
  DiffState,
  DSModal,
  DSNoteDisplay,
  DSNoteInput,
  DSNoteItem,
  DSTabs,
  NoteSection,
  SOAP_SECTIONS,
} from "../organisms";

export const OrganismsSection = () => {
  // Modal state
  const [basicModalOpen, setBasicModalOpen] = useState(false);
  const [actionsModalOpen, setActionsModalOpen] = useState(false);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [nestedModalOpen, setNestedModalOpen] = useState(false);
  const [modalDropdownValue, setModalDropdownValue] = useState<
    string | number | null
  >(null);

  // Tabs state
  const [horizontalTab, setHorizontalTab] = useState("drafts");
  const [verticalTab, setVerticalTab] = useState("profile");

  // Note input state
  const [soapNoteData, setSoapNoteData] = useState({
    subjective: "",
    objective: "",
    assessment: "",
    plan: "",
    additionalNotes: "",
  });
  const [freeformNoteData, setFreeformNoteData] = useState({
    content: "",
  });

  // Diff state for testing diff functionality
  const [diffNoteData, setDiffNoteData] = useState({
    subjective: "Patient reports mild headache for 2 days",
    objective: "BP: 120/80, HR: 72",
    assessment: "Tension headache",
    plan: "OTC pain relief, rest",
    additionalNotes: "",
  });
  const [diffState, setDiffState] = useState<
    Partial<Record<NoteSection, DiffState>>
  >({
    subjective: {
      before: "Patient reports mild headache for 2 days",
      after:
        "The patient presents with a mild headache that has persisted for two days. The headache is described as intermittent and manageable.",
      isLoading: false,
    },
  });

  const dropdownOptions = [
    { label: "Option 1", value: "option1" },
    { label: "Option 2", value: "option2" },
    { label: "Option 3", value: "option3" },
    {
      label: "Option 4 (Disabled)",
      value: "option4",
      disabled: true,
    },
    { label: "Option 5", value: "option5" },
    { label: "Option 6", value: "option6" },
  ];

  return (
    <>
      {/* Tabs Section */}
      <section className="mb-xl">
        <h2 className="mb-lg">Tabs</h2>

        <DSCardWrapper variant="ghost" dataTestId="ds-test-tabs-card">
          {/* Horizontal Tabs */}
          <div className="mb-lg">
            <h3>Horizontal (Default)</h3>
            <p className="text-secondary mb-md">
              Standard horizontal tab layout with customizable colors
              and badge counts.
            </p>
            <DSTabs
              value={horizontalTab}
              onChange={setHorizontalTab}
              dataTestId="ds-test-tabs-horizontal"
            >
              <DSTabs.List>
                <DSTabs.Tab
                  value="drafts"
                  badge={5}
                  activeColor="bg-indigo-200 dark:bg-indigo-900"
                >
                  Drafts
                </DSTabs.Tab>
                <DSTabs.Tab
                  value="processing"
                  badge={2}
                  activeColor="bg-yellow-200 dark:bg-yellow-900"
                >
                  Processing
                </DSTabs.Tab>
                <DSTabs.Tab
                  value="review"
                  badge={12}
                  activeColor="bg-green-200 dark:bg-green-900"
                >
                  For Review
                </DSTabs.Tab>
                <DSTabs.Tab
                  value="export"
                  activeColor="bg-blue-200 dark:bg-blue-900"
                >
                  Ready to Export
                </DSTabs.Tab>
                <DSTabs.Tab value="exported" disabled>
                  Exported (Disabled)
                </DSTabs.Tab>
              </DSTabs.List>

              <div>
                <DSTabs.Panel value="drafts">
                  <DSCardWrapper dataTestId="ds-test-tabs-panel-drafts">
                    <h4>Drafts Panel</h4>
                    <p className="text-secondary">
                      Content for drafts tab. You have 5 drafts
                      pending.
                    </p>
                  </DSCardWrapper>
                </DSTabs.Panel>
                <DSTabs.Panel value="processing">
                  <DSCardWrapper dataTestId="ds-test-tabs-panel-processing">
                    <h4>Processing Panel</h4>
                    <p className="text-secondary">
                      Content for processing tab. 2 items being
                      processed.
                    </p>
                  </DSCardWrapper>
                </DSTabs.Panel>
                <DSTabs.Panel value="review">
                  <DSCardWrapper dataTestId="ds-test-tabs-panel-review">
                    <h4>For Review Panel</h4>
                    <p className="text-secondary">
                      Content for review tab. 12 items ready for
                      review.
                    </p>
                  </DSCardWrapper>
                </DSTabs.Panel>
                <DSTabs.Panel value="export">
                  <DSCardWrapper dataTestId="ds-test-tabs-panel-export">
                    <h4>Ready to Export Panel</h4>
                    <p className="text-secondary">
                      Content for export tab. Items ready for export.
                    </p>
                  </DSCardWrapper>
                </DSTabs.Panel>
              </div>
            </DSTabs>
          </div>

          {/* Vertical Tabs */}
          <div className="mb-lg">
            <h3>Vertical Orientation</h3>
            <p className="text-secondary mb-md">
              Vertical layout for sidebars or settings pages.
            </p>
            <DSTabs
              value={verticalTab}
              onChange={setVerticalTab}
              orientation="vertical"
              dataTestId="ds-test-tabs-vertical"
            >
              <DSTabs.List>
                <DSTabs.Tab value="profile" icon={faUser}>
                  Profile
                </DSTabs.Tab>
                <DSTabs.Tab value="settings" icon={faGear}>
                  Settings
                </DSTabs.Tab>
                <DSTabs.Tab value="files" icon={faFolder}>
                  Files
                </DSTabs.Tab>
              </DSTabs.List>

              <div className="flex-1">
                <DSTabs.Panel value="profile">
                  <DSCardWrapper dataTestId="ds-test-tabs-panel-profile">
                    <h4>Profile Settings</h4>
                    <p className="text-secondary">
                      Manage your profile information here.
                    </p>
                  </DSCardWrapper>
                </DSTabs.Panel>
                <DSTabs.Panel value="settings">
                  <DSCardWrapper dataTestId="ds-test-tabs-panel-settings">
                    <h4>Application Settings</h4>
                    <p className="text-secondary">
                      Configure application preferences.
                    </p>
                  </DSCardWrapper>
                </DSTabs.Panel>
                <DSTabs.Panel value="files">
                  <DSCardWrapper dataTestId="ds-test-tabs-panel-files">
                    <h4>File Management</h4>
                    <p className="text-secondary">
                      Manage your uploaded files.
                    </p>
                  </DSCardWrapper>
                </DSTabs.Panel>
              </div>
            </DSTabs>
          </div>

          {/* Uncontrolled Tabs */}
          <div>
            <h3>Uncontrolled</h3>
            <p className="text-secondary mb-md">
              Using defaultValue for uncontrolled mode - no state
              management required.
            </p>
            <DSTabs
              defaultValue="tab1"
              dataTestId="ds-test-tabs-uncontrolled"
            >
              <DSTabs.List>
                <DSTabs.Tab value="tab1">Tab One</DSTabs.Tab>
                <DSTabs.Tab value="tab2">Tab Two</DSTabs.Tab>
                <DSTabs.Tab value="tab3">Tab Three</DSTabs.Tab>
              </DSTabs.List>

              <div>
                <DSTabs.Panel value="tab1">
                  <p className="p-md bg-secondary rounded-standard">
                    Content for Tab One
                  </p>
                </DSTabs.Panel>
                <DSTabs.Panel value="tab2">
                  <p className="p-md bg-secondary rounded-standard">
                    Content for Tab Two
                  </p>
                </DSTabs.Panel>
                <DSTabs.Panel value="tab3">
                  <p className="p-md bg-secondary rounded-standard">
                    Content for Tab Three
                  </p>
                </DSTabs.Panel>
              </div>
            </DSTabs>
          </div>
        </DSCardWrapper>
      </section>

      {/* Modals Section */}
      <section className="mb-xl">
        <h2 className="mb-lg">Modals</h2>

        <DSCardWrapper
          dataTestId="ds-test-modals-section"
          variant="ghost"
        >
          {/* Basic Modal */}
          <div className="mb-lg">
            <h3>Basic Modal</h3>
            <p className="text-secondary mb-md">
              Simple modal with title, subtitle, body text, and action
              buttons. Features Otto and Scribbles peeking!
            </p>
            <DSButton
              dataTestId="ds-test-btn-open-basic-modal"
              variant="primary"
              onClick={() => setBasicModalOpen(true)}
            >
              Open Basic Modal
            </DSButton>
            <DSModal
              dataTestId="ds-test-modal-basic"
              isOpen={basicModalOpen}
              onClose={() => setBasicModalOpen(false)}
              title="Are you a Scribenaut?"
              subtitle="Scribenauts only... this is super important!"
              actions={[
                {
                  label: "Cancel",
                  variant: "cancel",
                  onClick: () => setBasicModalOpen(false),
                },
                {
                  label: "Yes",
                  variant: "secondary",
                  onClick: () => {
                    alert("Yes clicked!");
                    setBasicModalOpen(false);
                  },
                },
                {
                  label: "Heck Yes!",
                  variant: "primary",
                  onClick: () => {
                    alert("Heck Yes clicked!");
                    setBasicModalOpen(false);
                  },
                },
              ]}
            >
              Read this only if you&apos;re a Scribenaut.
            </DSModal>
          </div>

          {/* Modal with Complex Content */}
          <div className="mb-lg">
            <h3>Modal with Complex Content</h3>
            <p className="text-secondary mb-md">
              Modal with more complex body content like checkboxes and
              forms.
            </p>
            <DSButton
              dataTestId="ds-test-btn-open-settings-modal"
              variant="primary"
              onClick={() => setCustomModalOpen(true)}
            >
              Open Settings Modal
            </DSButton>
            <DSModal
              dataTestId="ds-test-modal-settings"
              isOpen={customModalOpen}
              onClose={() => setCustomModalOpen(false)}
              title="Settings"
              actions={[
                {
                  label: "Cancel",
                  variant: "cancel",
                  onClick: () => setCustomModalOpen(false),
                },
                {
                  label: "Save",
                  variant: "primary",
                  onClick: () => {
                    alert("Settings saved!");
                    setCustomModalOpen(false);
                  },
                },
              ]}
            >
              <div className="space-y-md">
                <div>
                  <h4 className="text-primary font-semibold mb-sm">
                    Notification Preferences
                  </h4>
                  <div className="flex items-center gap-sm mb-sm">
                    <DSCheckbox
                      dataTestId="ds-test-modal-checkbox-email"
                      checked
                    />
                    <span className="text-primary">
                      Email notifications
                    </span>
                  </div>
                  <div className="flex items-center gap-sm">
                    <DSCheckbox dataTestId="ds-test-modal-checkbox-push" />
                    <span className="text-primary">
                      Push notifications
                    </span>
                  </div>
                </div>
                <div>
                  <h4 className="text-primary font-semibold mb-sm">
                    Privacy Settings
                  </h4>
                  <div className="flex items-center gap-sm mb-sm">
                    <DSCheckbox
                      dataTestId="ds-test-modal-checkbox-usage"
                      checked
                    />
                    <span className="text-primary">
                      Share usage data
                    </span>
                  </div>
                  <div className="flex items-center gap-sm">
                    <DSCheckbox
                      dataTestId="ds-test-modal-checkbox-analytics"
                      checked
                    />
                    <span className="text-primary">
                      Allow analytics
                    </span>
                  </div>
                </div>
                <div>
                  <h4 className="text-primary font-semibold mb-sm">
                    Default Template
                  </h4>
                  <p className="text-secondary text-xs mb-sm">
                    Dropdown inside modal
                  </p>
                  <DSDropdown
                    dataTestId="ds-test-modal-dropdown"
                    options={dropdownOptions}
                    value={modalDropdownValue}
                    onChange={(value) => setModalDropdownValue(value)}
                    placeholder="Select template"
                    search
                    searchPlaceholder="Search templates..."
                    width="w-full"
                  />
                </div>
              </div>
            </DSModal>
          </div>

          {/* Stacked Modals */}
          <div className="mb-lg">
            <h3>Stacked Modals</h3>
            <p className="text-secondary mb-md">
              Test scroll lock with nested modals. Background should
              not scroll while any modal is open.
            </p>
            <DSButton
              dataTestId="ds-test-btn-open-stacked-modal"
              variant="primary"
              onClick={() => setActionsModalOpen(true)}
            >
              Open Primary Modal
            </DSButton>
            <DSModal
              dataTestId="ds-test-modal-stacked-primary"
              isOpen={actionsModalOpen}
              onClose={() => setActionsModalOpen(false)}
              title="Primary Modal"
              subtitle="Open a nested modal to test scroll lock"
              actions={[
                {
                  label: "Close",
                  variant: "cancel",
                  onClick: () => setActionsModalOpen(false),
                },
                {
                  label: "Open Nested Modal",
                  variant: "primary",
                  onClick: () => setNestedModalOpen(true),
                },
              ]}
            >
              <p className="text-primary mb-md">
                This is the primary modal. Click &quot;Open Nested
                Modal&quot; to test stacked modal scroll lock.
              </p>
              <p className="text-secondary text-sm">
                When both modals are open, closing this one should
                keep scroll locked (nested still open). Closing the
                nested modal first should also keep scroll locked
                (this one still open). Scroll only unlocks when ALL
                modals close.
              </p>
            </DSModal>
            <DSModal
              dataTestId="ds-test-modal-stacked-nested"
              isOpen={nestedModalOpen}
              onClose={() => setNestedModalOpen(false)}
              title="Nested Modal"
              actions={[
                {
                  label: "Close Nested",
                  variant: "cancel",
                  onClick: () => setNestedModalOpen(false),
                },
                {
                  label: "Close Both",
                  variant: "primary",
                  onClick: () => {
                    setNestedModalOpen(false);
                    setActionsModalOpen(false);
                  },
                },
              ]}
            >
              <p className="text-primary">
                This is the nested modal. Try scrolling the background
                - it should be locked.
              </p>
            </DSModal>
          </div>
        </DSCardWrapper>
      </section>

      {/* Note Items Section */}
      <section className="mb-xl">
        <h2 className="mb-lg">Note Items</h2>

        <DSCardWrapper
          dataTestId="ds-test-note-items-section"
          variant="ghost"
        >
          {/* Status Badges */}
          <div className="mb-lg">
            <h3>Status Badges</h3>
            <p className="text-secondary mb-md">
              Badge color is determined by the status prop.
            </p>
            <div className="flex flex-wrap gap-lg items-start">
              <DSNoteItem
                dataTestId="ds-test-note-item-review"
                title="Julius: Urination issues"
                templateName="Template Name"
                date="Mon Dec. 29, 2025"
                status="review"
                onClick={() => alert("Note clicked!")}
              />
              <DSNoteItem
                dataTestId="ds-test-note-item-processing"
                title="Julius: Urination issues"
                templateName="Template Name"
                date="Mon Dec. 29, 2025"
                status="processing"
                onClick={() => alert("Note clicked!")}
              />
              <DSNoteItem
                dataTestId="ds-test-note-item-unprocessed"
                title="Julius: Urination issues"
                templateName="Template Name"
                date="Mon Dec. 29, 2025"
                status="unprocessed"
                onClick={() => alert("Note clicked!")}
              />
              <DSNoteItem
                dataTestId="ds-test-note-item-export"
                title="Julius: Urination issues"
                templateName="Template Name"
                date="Mon Dec. 29, 2025"
                status="export"
                onClick={() => alert("Note clicked!")}
              />
              <DSNoteItem
                dataTestId="ds-test-note-item-exported"
                title="Julius: Urination issues"
                templateName="Template Name"
                date="Mon Dec. 29, 2025"
                status="exported"
                onClick={() => alert("Note clicked!")}
              />
            </div>
          </div>

          {/* Compact Variant */}
          <div className="mb-lg">
            <h3>Compact Variant</h3>
            <p className="text-secondary mb-md">
              Smaller card for list views (no badge shown).
            </p>
            <div className="flex flex-wrap gap-lg items-start">
              <DSNoteItem
                dataTestId="ds-test-note-item-compact"
                title="Julius: Urination issues and hematuria evaluation"
                templateName="Template Name"
                date="Mon Dec. 29, 2025"
                size="compact"
                status="review"
                onClick={() => alert("Note clicked!")}
              />
            </div>
          </div>

          {/* Unsynced Variant */}
          <div className="mb-lg">
            <h3>Unsynced Variant</h3>
            <p className="text-secondary mb-md">
              Shows sync status with action button for offline notes.
              Unsynced status badge only shows when variant is also
              unsynced.
            </p>
            <div className="flex flex-wrap gap-lg items-start">
              <DSNoteItem
                dataTestId="ds-test-note-item-unsynced"
                title="Julius: Urination issues and hematuria evaluation"
                templateName="Template Name"
                date="Mon Dec. 29, 2025"
                variant="unsynced"
                status="unsynced"
                onSyncClick={() => alert("Sync button clicked!")}
              />
            </div>
          </div>
        </DSCardWrapper>
      </section>

      {/* Note Display Section */}
      <section className="mb-xl">
        <h2 className="mb-lg">Note Display</h2>

        <DSCardWrapper
          dataTestId="ds-test-note-display-section"
          variant="ghost"
        >
          {/* SOAP Note */}
          <div className="mb-lg">
            <h3>SOAP Note Display</h3>
            <p className="text-secondary mb-md">
              Displays clinical notes and templates in SOAP format
              with color-coded sections. Includes copy-to-clipboard
              for each section.
            </p>
            <DSNoteDisplay
              dataTestId="ds-test-note-display-soap"
              data={{
                subjective:
                  "3 y/o MN Golden Retriever presenting for vomiting and diarrhea x 2 days. Owner reports decreased appetite and lethargy. No known toxin exposure. Up to date on vaccines.",
                objective:
                  "T: 39.2°C, HR: 120 bpm, RR: 24. BCS 5/9. Mild dehydration (5%). Abdomen tense on palpation, no organomegaly. Mucous membranes pink, CRT < 2 sec.",
                assessment:
                  "Acute gastroenteritis, likely dietary indiscretion. DDx includes pancreatitis, foreign body, infectious enteritis.",
                plan: "1. SQ fluids 150ml\n2. Cerenia 1mg/kg SQ\n3. Bland diet (boiled chicken/rice) for 3-5 days\n4. Recheck if vomiting persists or worsens",
                additionalNotes:
                  "Owner counseled on dietary management. Call if lethargy worsens or blood noted in stool.",
              }}
            />
          </div>

          {/* Stacked Layout */}
          <div className="mb-lg">
            <h3>Stacked Layout</h3>
            <p className="text-secondary mb-md">
              Vertical stack for narrow containers or detailed view.
            </p>
            <DSNoteDisplay
              dataTestId="ds-test-note-display-stacked"
              layout="stacked"
              data={{
                subjective: "Vomiting and diarrhea x 2 days",
                objective: "T: 39.2°C, 5% dehydration",
                assessment: "Acute gastroenteritis",
                plan: "Fluids, Cerenia, bland diet",
              }}
            />
          </div>

          {/* Compact Size */}
          <div className="mb-lg">
            <h3>Compact Size</h3>
            <p className="text-secondary mb-md">
              Compact size for list views with tighter spacing.
            </p>
            <DSNoteDisplay
              dataTestId="ds-test-note-display-soap-compact"
              size="compact"
              data={{
                subjective: "Vomiting and diarrhea x 2 days",
                objective: "T: 39.2°C, 5% dehydration",
                assessment: "Acute gastroenteritis",
                plan: "Fluids, Cerenia, bland diet",
              }}
            />
          </div>

          {/* Template Example */}
          <div className="mb-lg">
            <h3>Template Preview</h3>
            <p className="text-secondary mb-md">
              Works for templates too - same SOAP structure.
            </p>
            <DSNoteDisplay
              dataTestId="ds-test-note-display-template"
              data={{
                subjective:
                  "[Signalment]\n[Chief complaint]\n[Duration and progression]\n[Relevant history]",
                objective:
                  "[Vitals: T, HR, RR, BCS]\n[Physical exam findings by system]",
                assessment:
                  "[Primary diagnosis]\n[Differential diagnoses]",
                plan: "[Diagnostics]\n[Treatments]\n[Client education]\n[Recheck timeline]",
              }}
            />
          </div>

          {/* Markdown Rendering */}
          <div className="mb-lg">
            <h3>Markdown Rendering</h3>
            <p className="text-secondary mb-md">
              Notes with markdown syntax are rendered with proper
              formatting (bold, italic, lists, headings).
            </p>
            <DSNoteDisplay
              dataTestId="ds-test-note-display-markdown"
              layout="stacked"
              data={{
                subjective:
                  "### History\n**3 y/o MN Golden Retriever** presenting for:\n- Vomiting x 2 days\n- Diarrhea x 2 days\n- *Decreased appetite* and lethargy\n\nNo known toxin exposure. Up to date on vaccines.",
                objective:
                  "**Vitals:**\n- T: 39.2°C\n- HR: 120 bpm\n- RR: 24\n- BCS: 5/9\n\n**Exam Findings:**\n1. Mild dehydration (5%)\n2. Abdomen tense on palpation\n3. No organomegaly\n\n> Mucous membranes pink, CRT < 2 sec",
                assessment:
                  "### Primary\nAcute gastroenteritis, likely **dietary indiscretion**\n\n### Differentials\n- Pancreatitis\n- Foreign body\n- Infectious enteritis",
                plan: "### Treatment Plan\n1. SQ fluids **150ml**\n2. Cerenia *1mg/kg* SQ\n3. Bland diet (boiled chicken/rice) for 3-5 days\n4. Recheck if vomiting persists or worsens",
              }}
            />
          </div>

          {/* Freeform Note */}
          <div className="mb-lg">
            <h3>Freeform Note</h3>
            <p className="text-secondary mb-md">
              Non-SOAP notes display as a single text block.
            </p>
            <DSNoteDisplay
              dataTestId="ds-test-note-display-freeform"
              variant="freeform"
              data={{
                content:
                  "Routine dental cleaning performed under general anesthesia. Full mouth radiographs taken - no significant findings. Grade 2 periodontal disease noted on upper premolars. All teeth scaled and polished. Fluoride treatment applied. Patient recovered well from anesthesia. Recommend dental chews and annual dental prophylaxis.",
              }}
            />
          </div>
        </DSCardWrapper>
      </section>

      {/* Note Input Section */}
      <section className="mb-xl">
        <h2 className="mb-lg">Note Input</h2>

        <DSCardWrapper
          dataTestId="ds-test-note-input-section"
          variant="ghost"
        >
          {/* SOAP Note Input */}
          <div className="mb-lg">
            <h3>SOAP Note Input</h3>
            <p className="text-secondary mb-md">
              Editable note input with rich text support for each SOAP
              section. Each section has its own toolbar for
              formatting.
            </p>
            <DSNoteInput
              dataTestId="ds-test-note-input-soap"
              data={soapNoteData}
              onChange={(data) =>
                setSoapNoteData({
                  subjective: data.subjective || "",
                  objective: data.objective || "",
                  assessment: data.assessment || "",
                  plan: data.plan || "",
                  additionalNotes: data.additionalNotes || "",
                })
              }
            >
              {SOAP_SECTIONS.map((section) => (
                <DSNoteInput.NoteField
                  key={section}
                  section={section}
                />
              ))}
            </DSNoteInput>
          </div>

          {/* Compact SOAP Input */}
          <div className="mb-lg">
            <h3>Compact SOAP Input</h3>
            <p className="text-secondary mb-md">
              Compact size for smaller viewports or embedded forms.
            </p>
            <DSNoteInput
              dataTestId="ds-test-note-input-soap-compact"
              data={{
                subjective: "Vomiting x 2 days",
                objective: "T: 39.2°C",
                assessment: "Gastroenteritis",
                plan: "Fluids, Cerenia",
              }}
              onChange={() => {}}
              size="compact"
            >
              {SOAP_SECTIONS.map((section) => (
                <DSNoteInput.NoteField
                  key={section}
                  section={section}
                />
              ))}
            </DSNoteInput>
          </div>

          {/* Freeform Note Input */}
          <div className="mb-lg">
            <h3>Freeform Note Input</h3>
            <p className="text-secondary mb-md">
              Single rich text editor for non-SOAP notes.
            </p>
            <DSNoteInput
              dataTestId="ds-test-note-input-freeform"
              data={freeformNoteData}
              onChange={(data) =>
                setFreeformNoteData({
                  content: data.content || "",
                })
              }
            >
              <DSNoteInput.NoteField section="freeform" />
            </DSNoteInput>
          </div>

          {/* With Action Buttons */}
          <div className="mb-lg">
            <h3>With Copy and Sparkle Buttons</h3>
            <p className="text-secondary mb-md">
              Each SOAP section has copy and AI writing assistant
              (sparkle) buttons.
            </p>
            <DSNoteInput
              dataTestId="ds-test-note-input-buttons"
              data={{
                subjective:
                  "3 y/o patient with vomiting and lethargy",
                objective: "T: 39.2°C, dehydrated",
                assessment: "Gastroenteritis",
                plan: "Fluids and monitoring",
              }}
              onChange={() => {}}
            >
              {SOAP_SECTIONS.map((section) => (
                <DSNoteInput.NoteField
                  key={section}
                  section={section}
                  showCopyButton
                  showSparkleButton
                  onSparkleClick={() =>
                    alert(`Sparkle clicked for ${section} section!`)
                  }
                />
              ))}
            </DSNoteInput>
          </div>

          {/* Freeform with Buttons */}
          <div className="mb-lg">
            <h3>Freeform with Action Buttons</h3>
            <p className="text-secondary mb-md">
              Freeform input also supports copy and sparkle buttons.
            </p>
            <DSNoteInput
              dataTestId="ds-test-note-input-freeform-buttons"
              data={{
                content:
                  "Patient presented for routine wellness exam. All parameters within normal limits.",
              }}
              onChange={() => {}}
            >
              <DSNoteInput.NoteField
                section="freeform"
                showCopyButton
                showSparkleButton
                onSparkleClick={() =>
                  alert("Sparkle clicked for freeform note!")
                }
              />
            </DSNoteInput>
          </div>

          {/* With Diff View */}
          <div className="mb-lg">
            <h3>With Diff View (AI Changes)</h3>
            <p className="text-secondary mb-md">
              Shows side-by-side comparison when AI generates improved
              content. Accept/Reject buttons allow users to choose
              which version to keep.
            </p>
            <DSNoteInput
              dataTestId="ds-test-note-input-diff"
              data={diffNoteData}
              onChange={(data) =>
                setDiffNoteData({
                  subjective: data.subjective || "",
                  objective: data.objective || "",
                  assessment: data.assessment || "",
                  plan: data.plan || "",
                  additionalNotes: data.additionalNotes || "",
                })
              }
            >
              {SOAP_SECTIONS.map((section) => {
                const sectionDiff = diffState[section];
                return sectionDiff ? (
                  <DSNoteInput.NoteField
                    key={section}
                    section={section}
                    diffState={sectionDiff}
                    onDiffAccept={() => {
                      // Accept: Update the actual field with the "after" content
                      if (sectionDiff.after) {
                        setDiffNoteData({
                          ...diffNoteData,
                          [section]: sectionDiff.after,
                        });
                      }
                      // Clear diff state for this section
                      setDiffState({
                        ...diffState,
                        [section]: undefined,
                      });
                      alert(
                        `Accepted changes for ${section} section`,
                      );
                    }}
                    onDiffReject={() => {
                      // Reject: Keep original content, just clear diff state
                      setDiffState({
                        ...diffState,
                        [section]: undefined,
                      });
                      alert(
                        `Rejected changes for ${section} section`,
                      );
                    }}
                  />
                ) : (
                  <DSNoteInput.NoteField
                    key={section}
                    section={section}
                  />
                );
              })}
            </DSNoteInput>
          </div>

          {/* Compact Diff View */}
          <div className="mb-lg">
            <h3>Compact Diff View</h3>
            <p className="text-secondary mb-md">
              Compact mode shows diff with letter badges and stacked
              layout.
            </p>
            <DSNoteInput
              dataTestId="ds-test-note-input-diff-compact"
              data={{
                subjective: "Headache x 2 days",
                objective: "BP 120/80",
                assessment: "Tension headache",
                plan: "OTC meds",
              }}
              onChange={() => {}}
              size="compact"
            >
              {SOAP_SECTIONS.map((section) =>
                section === "objective" ? (
                  <DSNoteInput.NoteField
                    key={section}
                    section={section}
                    diffState={{
                      before: "BP 120/80",
                      after:
                        "Blood pressure: 120/80 mmHg (within normal limits)\nHeart rate: 72 bpm\nRespiratory rate: 16/min",
                      isLoading: false,
                    }}
                    onDiffAccept={() =>
                      alert(`Accepted ${section} changes`)
                    }
                    onDiffReject={() =>
                      alert(`Rejected ${section} changes`)
                    }
                  />
                ) : (
                  <DSNoteInput.NoteField
                    key={section}
                    section={section}
                  />
                ),
              )}
            </DSNoteInput>
          </div>
        </DSCardWrapper>
      </section>
    </>
  );
};
